import { motion } from 'framer-motion'
import { useState } from 'react'
import { Check, Eye, MessageSquare, ShieldCheck, AlertTriangle } from 'lucide-react'

interface StepValidation {
  match: boolean
  confidence: number
  discrepancies?: string[]
}

interface Props {
  confirmed: boolean
  onConfirm: () => void
  onReject: (feedback: string) => void
  stepValidation?: StepValidation
}

export default function AgentPartValidation({ confirmed, onConfirm, onReject, stepValidation }: Props) {
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedback, setFeedback] = useState('')

  const validated = stepValidation?.match || (stepValidation && stepValidation.confidence >= 80)
  const hasDiscrepancies = stepValidation && !validated && stepValidation.discrepancies && stepValidation.discrepancies.length > 0

  if (confirmed) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        className="flex items-center gap-2 py-0.5"
      >
        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="font-mono text-xs text-slate-500">Part preview confirmed</span>
      </motion.div>
    )
  }

  const handleRejectSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!feedback.trim()) return
    onReject(feedback.trim())
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="my-2 rounded-lg border border-cyan-500/20 bg-white/[0.02] px-4 py-3"
    >
      <div className="flex items-start gap-2 mb-3">
        <Eye className="w-3.5 h-3.5 shrink-0 mt-0.5 text-cyan-400" />
        <span className="font-mono text-sm text-slate-200">
          Does the finished part look correct?
        </span>
      </div>

      {/* Validation badge */}
      {stepValidation ? (
        validated ? (
          <div className="flex items-center gap-2 mb-3 ml-5 px-2.5 py-1.5 rounded-lg bg-emerald-500/5 border border-emerald-500/20 w-fit">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
            <span className="font-mono text-xs text-emerald-400">
              Validated — {stepValidation.confidence}% confidence
            </span>
          </div>
        ) : (
          <div className="mb-3 ml-5 space-y-1.5">
            <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-amber-500/5 border border-amber-500/20 w-fit">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
              <span className="font-mono text-xs text-amber-400">
                Review recommended — {stepValidation.confidence}% confidence
              </span>
            </div>
            {hasDiscrepancies && (
              <ul className="ml-6 space-y-0.5">
                {stepValidation.discrepancies!.map((d, i) => (
                  <li key={i} className="font-mono text-xs text-slate-400">
                    - {d}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      ) : (
        <p className="font-mono text-xs text-slate-500 mb-3 ml-5">
          Check the 3D preview on the right
        </p>
      )}

      {!showFeedback ? (
        <div className="flex gap-2 ml-5">
          <button
            type="button"
            onClick={onConfirm}
            className="px-3 py-1.5 rounded-lg font-mono text-xs font-semibold border border-emerald-500/30 text-emerald-400 hover:border-emerald-400 hover:bg-emerald-500/10 transition-all duration-150"
          >
            Looks Good
          </button>
          <button
            type="button"
            onClick={() => setShowFeedback(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-mono text-xs border border-slate-600 text-slate-400 hover:border-cyan-500/40 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all duration-150"
          >
            <MessageSquare className="w-3 h-3" />
            Something's Wrong
          </button>
        </div>
      ) : (
        <div className="ml-5 space-y-2">
          <form onSubmit={handleRejectSubmit} className="flex gap-2">
            <input
              type="text"
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="Describe what's wrong..."
              autoFocus
              className="flex-1 bg-white/5 border border-cyan-500/20 rounded-lg px-3 py-1.5 font-mono text-sm text-slate-200 placeholder:text-slate-600 focus:border-cyan-400 focus:outline-none transition-colors"
            />
            <button
              type="submit"
              className="px-3 py-1.5 rounded-lg font-mono text-xs font-semibold border border-cyan-500/30 text-cyan-400 hover:border-cyan-400 hover:bg-cyan-500/10 transition-all duration-150"
            >
              Send
            </button>
          </form>
          <button
            type="button"
            onClick={() => { setShowFeedback(false); setFeedback('') }}
            className="font-mono text-xs text-slate-500 hover:text-slate-300 transition-colors"
          >
            Never mind, it looks fine
          </button>
        </div>
      )}
    </motion.div>
  )
}
